import { useQuery } from "@tanstack/react-query";
import { ChartWrapper, LineChart, getDateFormatByCount } from "components/charts";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  type QueryFilter,
  defaultDateFilter,
  isUserDeleted,
  formatUserName,
  lineChartFactory,
  noData,
  validLineData,
  createDateRange,
} from "features/Library/Common/utils/performanceUtils";
import { sharedAccountReportingLineProps } from "features/Reporting/Content/shared";
import { GenericPerformanceList } from "features/Reporting/Content/shared/GenericPerformanceList";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import { type ColumnOption } from "interfaces/columnOptions";
import { useMemo, useState } from "react";
import dateTimeUtils from "utils/dateTimeUtils";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import "../peopleStyles.scss";
import { Tooltip } from "components/common/tooltip";
import { TextTruncate } from "components";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";

const getPeopleLineChart = async ({ queryKey, signal }: QueryFilter) => {
  const result = await peopleReportingService.users.getLineChart(queryKey[1], signal!);

  return lineChartFactory(result.data);
};

const getPeopleCards = async ({ queryKey, signal }: QueryFilter): Promise<CardReportingItem[]> => {
  const { data } = await peopleReportingService.users.getStatistics(queryKey[1], signal!);

  return [
    {
      stat: data.Users,
      popupBody: "The total number of users for this account within within the specified date range.",
      statDescription: "Users",
    },
    {
      stat: data.Departments,
      popupBody: "The total number of departments for this account within the specified date range.",
      statDescription: "Departments",
    },
    {
      stat: data.Groups,
      popupBody: "The total number of groups for this account within the specified date range.",
      statDescription: "Groups",
    },
    {
      stat: data.Licenses,
      popupBody: "The total number of licenses granted to users of this account within the specified date range.",
      statDescription: "Licenses",
    },
  ];
};

type Re = {
  FirstName: string | null;
  LastName: string | null;
  id: number | null;
  platformOutreach: number;
  userInteractions: number;
  lastActivity: string;
};

const getPeopleTable = async ({ queryKey, signal }: QueryFilter): Promise<Re[]> => {
  const result = await peopleReportingService.users.getTable(queryKey[1], signal!);

  return result.data.map((r) => ({
    FirstName: r.FirstName,
    LastName: r.LastName,
    id: r.UserId,
    platformOutreach: r.PlatformOutreach,
    userInteractions: r.PlatformInteraction,
    lastActivity: r.LastActivity ? dateTimeUtils.formatDate(r.LastActivity) : "-",
  }));
};

const columns: ColumnOption<Re>[] = [
  {
    name: "Name",
    width: 7,
    render(item) {
      const shouldShowLink = !isUserDeleted(item.FirstName, item.LastName) && item.id !== null;
      const formattedName = formatUserName(item.FirstName, item.LastName);

      return (
        <Tooltip
          target={
            <TextTruncate>
              {shouldShowLink ? (
                <MoboLink to={`/${RouteNames.peopleUsers}/${item.id}`}>{formattedName}</MoboLink>
              ) : (
                formattedName
              )}
            </TextTruncate>
          }
          content={formattedName}
        />
      );
    },
  },
  {
    name: "Platform Outreach",
    width: 7,
    render(item) {
      return <span>{(item.platformOutreach ?? 0).toLocaleString()}</span>;
    },
  },
  {
    name: "Platform Interactions",
    width: 7,
    render(item) {
      return <span>{(item.userInteractions ?? 0).toLocaleString()}</span>;
    },
  },
  {
    name: "Last Activity",
    width: 7,
    render(item) {
      return <span>{item.lastActivity}</span>;
    },
  },
];

export const UsersReport = () => {
  const [dateFilter, setDateFilter] = useState(defaultDateFilter());

  const dateRange = useMemo(
    () => createDateRange(dateFilter.dateFrom, dateFilter.dateTo),
    [dateFilter.dateFrom, dateFilter.dateTo],
  );

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["people users line query", { ...dateFilter, aggregation: chartPeriod }],
    queryFn: getPeopleLineChart,
    enabled: chartPeriod !== CHART_PERIOD.UNSET,
  });

  const cardQuery = useQuery({
    queryKey: ["people users cards", dateFilter],
    queryFn: getPeopleCards,
  });

  const tableQuery = useQuery({
    queryKey: ["people users table query", dateFilter],
    queryFn: getPeopleTable,
  });

  return (
    <div className="performanceRoot">
      <div className="performanceHeader peopleActivityHeader">
        <h2 className="performanceTitle">Summary</h2>
        <div>
          <ReportingFilter currentFilter={dateFilter} callback={setDateFilter} includeAccounts={false} />
        </div>
      </div>
      <div className="performanceBody">
        <div className="lineChartContainer" ref={measureRef}>
          <ChartWrapper titles={["Active Users"]}>
            <RequestStatusRenderer state={lineChartQuery.status}>
              {lineChartQuery.isSuccess && validLineData([lineChartQuery.data.UserCount]) ? (
                <LineChart
                  {...sharedAccountReportingLineProps}
                  xData={[lineChartQuery.data.Date]}
                  yData={[lineChartQuery.data.UserCount]}
                  xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
                />
              ) : (
                noData(dateFilter)
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </div>
        <div className="cards">
          <RequestStatusRenderer state={cardQuery.status}>
            <CardReporting items={cardQuery.data!} />
          </RequestStatusRenderer>
        </div>
        <RequestStatusRenderer state={tableQuery.status}>
          {tableQuery.isSuccess && (
            <GenericPerformanceList title="People" rows={tableQuery.data} columns={columns} filter={dateFilter} />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};
